import React, { useState } from 'react';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { slotsAPI, coursesAPI, usersAPI } from '../../services/api';
import { useToast } from '../../context/ToastContext';
import { useEscapeKey } from '../../hooks/useEscapeKey';
import CancellationRecipientsModal from './CancellationRecipientsModal';
import type { CancellationInfo } from '../../types';

interface ChildSlot {
  id: string;
  title: string;
  description?: string | null;
  date: string;
  startTime: string;
  endTime?: string | null;
  capacity: number;
  cancelled?: boolean;
  courseId?: string | null;
  trainerId?: string | null;
  isSpecial?: boolean;
  isCompetition?: boolean;
}

interface EditChildModalProps {
  child: ChildSlot;
  onClose: () => void;
  onSaved?: () => void;
}

const EditChildModal: React.FC<EditChildModalProps> = ({ child, onClose, onSaved }) => {
  const { t } = useTranslation();
  const [cancelled, setCancelled] = useState(child.cancelled || false);
  const [isSpecial, setIsSpecial] = useState(child.isSpecial || false);
  const [isCompetition, setIsCompetition] = useState(child.isCompetition || false);
  const [courseId, setCourseId] = useState<string>(child.courseId || '');
  const [trainerId, setTrainerId] = useState<string>(child.trainerId || '');
  const [cancellationInfo, setCancellationInfo] = useState<CancellationInfo | null>(null);
  const queryClient = useQueryClient();
  const toast = useToast();

  const { data: courses = [] } = useQuery({
    queryKey: ['courses'],
    queryFn: coursesAPI.getCourses,
  });

  const { data: users = [] } = useQuery({
    queryKey: ['users'],
    queryFn: usersAPI.getUsers,
  });

  const trainers = users.filter((u: any) => u.role === 'TRAINER' || u.role === 'ADMIN' || u.role === 'POWER_ADMIN');

  useEscapeKey(onClose);

  const updateMutation = useMutation({
    mutationFn: (data: any) => slotsAPI.updateSlot(child.id, data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['series-children'] });
      queryClient.invalidateQueries({ queryKey: ['slots'] });
      toast.success(t('admin.occurrenceUpdated'));
      onSaved?.();
      if (data.cancellation) {
        // Cancellation just happened — keep this modal's shell mounted so we
        // can show the recipients popup instead of closing straight away.
        setCancellationInfo(data.cancellation);
      } else {
        onClose();
      }
    },
    onError: () => {
      toast.error(t('admin.failedToUpdateOccurrence'));
    },
  });

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    updateMutation.mutate({
      title: fd.get('title') as string,
      description: fd.get('description') as string || undefined,
      startTime: fd.get('startTime') as string,
      endTime: (fd.get('endTime') as string) || undefined,
      capacity: parseInt(fd.get('capacity') as string),
      cancelled,
      isSpecial,
      isCompetition,
      courseId: courseId || null,
      trainerId: trainerId || null,
    });
  };

  if (cancellationInfo) {
    return (
      <CancellationRecipientsModal
        cancellation={cancellationInfo}
        onClose={() => { setCancellationInfo(null); onClose(); }}
      />
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4 p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-semibold">
            {t('admin.editOccurrence')} — {format(new Date(child.date), 'dd MMM yyyy')}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">
            &times;
          </button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('admin.title')}</label>
            <input type="text" name="title" required className="w-full border rounded px-3 py-2"
              defaultValue={child.title} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('admin.startTime')}</label>
              <input type="time" name="startTime" required className="w-full border rounded px-3 py-2"
                defaultValue={child.startTime} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('admin.endTime')}</label>
              <input type="time" name="endTime" className="w-full border rounded px-3 py-2"
                defaultValue={child.endTime || ''} />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('admin.capacity')}</label>
            <input type="number" name="capacity" required min="1" className="w-full border rounded px-3 py-2"
              defaultValue={child.capacity} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('admin.description')}</label>
            <textarea name="description" className="w-full border rounded px-3 py-2" rows={2}
              defaultValue={child.description || ''} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('admin.route')}</label>
            <select
              value={courseId}
              onChange={(e) => setCourseId(e.target.value)}
              className="w-full border rounded px-3 py-2"
            >
              <option value="">{t('admin.noRouteOption')}</option>
              {courses.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('admin.trainerOrLeader')}</label>
            <select
              value={trainerId}
              onChange={(e) => setTrainerId(e.target.value)}
              className="w-full border rounded px-3 py-2"
            >
              <option value="">{t('admin.noTrainerOption')}</option>
              {trainers.map((u: any) => (
                <option key={u.id} value={u.id}>
                  {u.name} {u.surname} ({u.role})
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="isSpecial"
                checked={isSpecial}
                onChange={(e) => setIsSpecial(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-orange-600 focus:ring-orange-500"
              />
              <label htmlFor="isSpecial" className="text-sm font-medium text-orange-600">
                {t('admin.isSpecialLabel')}
              </label>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="isCompetition"
                checked={isCompetition}
                onChange={(e) => setIsCompetition(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <label htmlFor="isCompetition" className="text-sm font-medium text-blue-600">
                {t('admin.isCompetitionLabel')}
              </label>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="cancelled"
                checked={cancelled}
                onChange={(e) => setCancelled(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-red-600 focus:ring-red-500"
              />
              <label htmlFor="cancelled" className="text-sm font-medium text-red-600">
                {t('admin.cancelThisOccurrence')}
              </label>
            </div>
          </div>
          <div className="flex gap-3 pt-2">
            <button type="submit" disabled={updateMutation.isPending}
              className="bg-primary-600 text-white px-6 py-2 rounded hover:bg-primary-700 disabled:bg-gray-400">
              {updateMutation.isPending ? t('admin.saving') : t('admin.save')}
            </button>
            <button type="button" onClick={onClose}
              className="border border-gray-300 text-gray-700 px-6 py-2 rounded hover:bg-gray-50">
              {t('admin.cancel')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditChildModal;
